import { useId, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/lib/api";
import { oibIspravan, OIB_PORUKA } from "@/lib/oib";
import { createHistoricalWorker, type HistoricalWorkerInput, type Radnik } from "@/lib/radnici";

export function HistoricalWorkerCreate({ onCreated, disabled = false }: { onCreated: (worker: Radnik) => void; disabled?: boolean }) {
  const { can } = useAuth();
  const id = useId();
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState({ ime: "", prezime: "", oib: "", datum_rodenja: "", broj_putovnice: "", datum_prestanka: "" });
  const [errors, setErrors] = useState<Partial<Record<keyof HistoricalWorkerInput, string>>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const sending = useRef(false);
  if (!can("radnik", "create") && !can("dokument", "inbox_upload")) return null;
  async function save() {
    if (disabled || sending.current) return;
    const body: HistoricalWorkerInput = {
      ime: values.ime.trim(), prezime: values.prezime.trim(), oib: values.oib.trim() || null,
      datum_rodenja: values.datum_rodenja || null, broj_putovnice: values.broj_putovnice.trim() || null,
      datum_prestanka: values.datum_prestanka || null,
    };
    const next: typeof errors = {};
    if (!body.ime) next.ime = "Unesite ime.";
    if (!body.prezime) next.prezime = "Unesite prezime.";
    if (body.oib && !oibIspravan(body.oib)) next.oib = OIB_PORUKA;
    if (!body.oib && !body.datum_rodenja && !body.broj_putovnice) next.oib = "Unesite OIB, datum rođenja ili broj putovnice.";
    setErrors(next); setError(null);
    if (Object.keys(next).length) return;
    sending.current = true; setBusy(true);
    try {
      const worker = await createHistoricalWorker(body);
      if (!worker.id) throw new Error("Poslužitelj nije vratio identifikator radnika.");
      onCreated(worker); setOpen(false);
      setValues({ ime: "", prezime: "", oib: "", datum_rodenja: "", broj_putovnice: "", datum_prestanka: "" });
    } catch (cause) { setError(errorMessage(cause, "Povijesni radnik nije spremljen.")); }
    finally { sending.current = false; setBusy(false); }
  }
  return <section className="col-span-full space-y-3" aria-label="Novi povijesni radnik">
    <Button type="button" variant="outline" disabled={disabled || busy} aria-expanded={open} onClick={() => setOpen((value) => !value)}>Novi povijesni radnik</Button>
    {open && <div className="avm-surface space-y-3 rounded border p-3" onKeyDown={(event) => {
      if (event.key === "Enter" && event.target instanceof HTMLInputElement) { event.preventDefault(); event.stopPropagation(); void save(); }
    }}>
      <p className="text-sm">Ime i prezime su obvezni. Za identifikaciju unesite OIB ili barem datum rođenja ili broj putovnice.</p>
      <div className="grid gap-3 sm:grid-cols-2">{([
        ["ime", "Ime", "text"], ["prezime", "Prezime", "text"], ["oib", "OIB", "text"],
        ["datum_rodenja", "Datum rođenja", "date"], ["broj_putovnice", "Broj putovnice", "text"], ["datum_prestanka", "Datum prestanka", "date"],
      ] as const).map(([key, label, type]) => <div key={key} className="space-y-1 text-sm"><label data-required-label htmlFor={`${id}-${key}`}>
        {label}{key === "ime" || key === "prezime" ? <span className="text-[var(--color-status-bad-fg)]"> *</span> : null}
        </label>
        <Input id={`${id}-${key}`} type={type} value={values[key]} disabled={disabled || busy} maxLength={key === "oib" ? 11 : key === "broj_putovnice" ? 120 : 80}
          aria-required={key === "ime" || key === "prezime"} aria-invalid={!!errors[key]} aria-describedby={errors[key] ? `${id}-${key}-error` : undefined}
          className={errors[key] ? "border-[var(--color-status-bad-fg)]" : ""} onChange={(event) => {
            setValues((current) => ({ ...current, [key]: event.target.value }));
            setErrors((current) => ({ ...current, [key]: undefined, ...(!values.oib.trim() && (key === "datum_rodenja" || key === "broj_putovnice") ? { oib: undefined } : {}) }));
            setError(null);
          }} />
        {errors[key] && <span id={`${id}-${key}-error`} className="text-[var(--color-status-bad-fg)]">{errors[key]}</span>}
      </div>)}</div>
      {error && <p role="alert">{error}</p>}
      <div className="flex flex-wrap gap-2"><Button type="button" disabled={disabled || busy} onClick={() => void save()}>{busy ? "Spremanje…" : "Spremi povijesnog radnika"}</Button><Button type="button" variant="outline" disabled={busy} onClick={() => setOpen(false)}>Odustani od unosa</Button></div>
    </div>}
  </section>;
}
